import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { ellipseAddress } from 'src/helpers';
import { format6SignificantFigures } from 'src/helpers/formatNumber';
import {
  Participant,
  useGetCurrentLeaderboard,
} from 'src/services/leaderboard/get-current-leaderboard';
import {
  Season,
  useGetPreviousLeaderboard,
} from 'src/services/leaderboard/get-previous-leaderboards';
import { useGetUserScore } from 'src/services/leaderboard/get-user-score';
import { emojiAvatarForAddress } from 'src/utils/emojiAvatarForAddress';
import CustomPieChart from './components/CustomPieChart';
import Table from './components/CustomTable';
import { Upload } from 'lucide-react';
import SharePointModal from './components/SharePointModal';
import { cn } from 'src/components/ui/lib/utils';
import useWindowSize from 'src/hooks/useWindowSize';
import CardItem from './components/CardItem';
import { useGetLeaderboardById } from 'src/services/leaderboard/get-leaderboard-by-id';
import { useAuth } from 'src/libs/rainbow';
import { useAppTranslation } from 'src/i18n/hooks';
import { message, notification, Pagination, Skeleton } from 'antd';
import { useGetAllTimeLeaderboard } from 'src/services/leaderboard/get-all-time-leaderboard';
import {
  CyberpunkExplosion,
  CyberpunkParticle,
  DigitalGlitch,
  DigitalWave,
  MatrixRain,
} from './components/Effects';
import { useClaimScore } from 'src/services/leaderboard/claim-score';
import { useGetXPLeaderboard } from 'src/services/leaderboard/get-xp-leaderboard';
import TierBadge, { getTierConfig } from './components/TierBadge';
import CelebrationModal from './components/CelebrationModal';
import BigNumber from 'bignumber.js';
import useGetUserAddress from 'src/hooks/useGetUserAddress';
import { useGetPreseasonLeaderboard } from 'src/services/leaderboard/get-preseason-leaderboard';

const pageSize = 10;

export const getTierImage = (tier?: string) => {
  switch (tier) {
    case 'Whale Shark':
      return '/images/whale-shark.png';
    case 'Great White':
      return '/images/great-white.png';
    case 'Hammerhead':
      return '/images/hammer-head.png';
    case 'Swordfish':
      return '/images/sword-fish.png';
    case 'Clownfish':
      return '/images/clown-fish.png';
    case 'Shrimp':
      return '/images/shrimp.png';
    default:
      break;
  }
};

export const getTierImageByPoint = (point: number) => {
  if (point > 500000) {
    return '/images/whale-shark.png';
  } else if (point > 100000) {
    return '/images/great-white.png';
  } else if (point > 10000) {
    return '/images/hammer-head.png';
  } else if (point > 1000) {
    return '/images/sword-fish.png';
  } else if (point > 100) {
    return '/images/clown-fish.png';
  } else if (point > 10) {
    return '/images/shrimp.png';
  }
};

export const getTierNameByPoint = (point: number) => {
  if (point > 500000) {
    return 'Whale Shark';
  } else if (point > 100000) {
    return 'Great White';
  } else if (point > 10000) {
    return 'Hammerhead';
  } else if (point > 1000) {
    return 'Swordfish';
  } else if (point > 100) {
    return 'Clownfish';
  } else if (point > 10) {
    return 'Shrimp';
  }
  return '';
};

const TradingLeaderboard: React.FC = () => {
  const { t } = useAppTranslation('leaderboard');
  const [isExploding, setIsExploding] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [particles, setParticles] = useState<
    Array<{ x: number; y: number; delay: number; type: string }>
  >([]);
  const [claimedPoints, setClaimedPoints] = useState<number>();
  const [totalPoints, setTotalPoints] = useState<number>();
  const { address } = useGetUserAddress();

  const [selectedWeek, setSelectedWeek] = useState<Season>();
  const [dataType, setDataType] = useState<
    'isFirstWeek' | 'isCurrentWeek' | 'isAll' | 'isPreviousWeek' | 'isPreseason'
  >('isCurrentWeek');
  const [page, setPage] = useState(1);
  const { isAuthenticated, isEthereumWallet } = useAuth();

  const { data: firstWeekLeaderboard } = useGetCurrentLeaderboard({
    page: 1,
    page_size: pageSize,
  });

  const [availableOptionLoading, setAvailableOptionLoading] = useState(true);
  const { data: availableSeason, isLoading: isLoadingAvailableSeason } =
    useGetPreviousLeaderboard();

  const [openSharePointModal, setOpenSharePointModal] = useState<boolean>(false);
  const [openClaimSuccessModal, setOpenClaimSuccessModal] = useState<boolean>(false);

  const { data: leaderboardRawData } = useGetLeaderboardById(
    selectedWeek?.id ?? '',
    {
      page: page,
      page_size: pageSize,
    },
    isEthereumWallet ? address : undefined,
    {
      enabled: dataType === 'isPreviousWeek',
    },
  );

  const {
    data: AllTimeLeaderboardData,
    refetch: refetchAllTimeLeaderboard,
    isFetching: allTimeDataFetching,
  } = useGetAllTimeLeaderboard(
    {
      page: page,
      page_size: pageSize,
      useraddress: isEthereumWallet ? address : undefined,
    },
    {
      enabled: dataType === 'isAll',
    },
  );

  const {
    data: preseasonLeaderboard,
    refetch: refetchPreseasonLeaderboard,
    isFetching: preseasonDataFetching,
  } = useGetPreseasonLeaderboard(
    {
      page: page,
      page_size: pageSize,
      user: isEthereumWallet ? address : undefined,
    },
    {
      enabled: dataType === 'isPreseason',
    },
  );

  const { isMobile } = useWindowSize();

  const dashboardTimeRange = useMemo(() => {
    // get time range for first week
    if (firstWeekLeaderboard && moment(firstWeekLeaderboard.end_date).isAfter(moment())) {
      return {
        startTime: firstWeekLeaderboard.start_date,
        endDate: firstWeekLeaderboard.end_date,
        isNotFirstWeek: false,
      };
    }

    return {
      startTime: moment(
        availableSeason?.leaderboards?.[0]?.end_date ?? firstWeekLeaderboard?.end_date,
      ).toISOString(),
      endDate: moment(availableSeason?.leaderboards?.[0]?.end_date).add(7, 'day').toISOString(),
      isNotFirstWeek: true,
    };
  }, [firstWeekLeaderboard, selectedWeek, availableSeason]);

  const {
    data: xpLeaderboardData,
    refetch: refetchCurrentWeek,
    isFetching: xpLeaderBoardFetching,
  } = useGetXPLeaderboard(
    {
      page: page,
      page_size: pageSize,
      useraddress: isEthereumWallet ? address : undefined,
      start_date: moment(dashboardTimeRange.startTime).utc().toISOString(),
      end_date: moment(dashboardTimeRange.endDate).utc().toISOString(),
    },
    {
      enabled: dataType === 'isCurrentWeek',
    },
  );

  const { mutateAsync: claimScore } = useClaimScore();

  const leaderBoardData = useMemo(() => {
    if (dataType === 'isCurrentWeek') {
      return xpLeaderboardData;
    }

    if (dataType === 'isFirstWeek') return firstWeekLeaderboard;

    if (dataType === 'isAll') {
      return AllTimeLeaderboardData;
    }

    if (dataType === 'isPreseason') {
      return preseasonLeaderboard;
    }

    // get time range for first week
    if (firstWeekLeaderboard && moment(firstWeekLeaderboard.end_date).isAfter(moment())) {
      return firstWeekLeaderboard;
    }
    return leaderboardRawData;
  }, [
    firstWeekLeaderboard,
    AllTimeLeaderboardData,
    leaderboardRawData,
    dataType,
    xpLeaderboardData,
    preseasonLeaderboard,
  ]);

  const { data: userScore, refetch: refetchUserScore } = useGetUserScore(
    {
      from: moment(dashboardTimeRange?.startTime).utc().format('YYYY-MM-DDTHH:mm:ss'),
    },
    {
      refetchInterval: 30000, //30s
    },
  );
  // highlight current user and sticky on top table

  const columns: ColumnDef<Participant>[] = [
    {
      accessorKey: 'rank',
      header: t('rank'),
      size: 50,
      cell: ({ row: _row }) => (
        <div className="h-[46px] flex items-center">
          <p className=" tracking-wider group-hover:text-cyan-300 transition-colors">
            {_row.original.rank}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'tier',
      header: t('tier'),
      size: 100,
      cell: ({ row: _row }) => (
        <div>{_row.original.tier && <TierBadge tier={_row.original.tier} />}</div>
      ),
    },
    {
      accessorKey: 'wallet_address',
      header: t('trader'),
      size: 100,
      cell: ({ row: _row }) => {
        return (
          <div className="flex gap-[8px]">
            <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
              {ellipseAddress(_row.original.wallet_address)}{' '}
              {_row.original.wallet_address === address ? '(You)' : ''}
            </p>
          </div>
        );
      },
    },
    // {
    //   accessorKey: 'account_equity',
    //   header: t('accountValue'),
    //   size: 100,
    //   cell: ({ row: _row }) => (
    //     <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
    //       ${format6SignificantFigures(_row.original.account_equity ?? 0)}
    //     </p>
    //   ),
    // },
    // {
    //   accessorKey: 'pnl',
    //   header: t('pnl'),
    //   size: 100,
    //   cell: ({ row: _row }) => (
    //     <p
    //       className={cn([
    //         'tracking-wider group-hover:text-cyan-300 transition-colors',
    //         _row.original.pnl >= 0 ? 'text-green-400' : 'text-red-400',
    //       ])}
    //     >
    //       ${format6SignificantFigures(_row.original.pnl)}
    //     </p>
    //   ),
    // },
    // {
    //   accessorKey: 'roi',
    //   header: t('roi'),
    //   size: 100,
    //   cell: ({ row: _row }) => (
    //     <p
    //       className={cn([
    //         'tracking-wider group-hover:text-cyan-300 transition-colors',
    //         _row.original.roi >= 0 ? 'text-green-400' : 'text-red-400',
    //       ])}
    //     >
    //       {nFormatterV2(_row.original.roi.toString(), 2)}%
    //     </p>
    //   ),
    // },
    {
      accessorKey: 'point',
      header: t('points'),
      size: 100,
      cell: ({ row: _row }) => (
        <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
          {_row.original.points?.toLocaleString('en-US', {
            maximumFractionDigits: 4,
            minimumFractionDigits: 0,
          })}
        </p>
      ),
    },
  ];

  const xpLeaderboardColumns: ColumnDef<Participant>[] = [
    {
      accessorKey: 'rank',
      header: t('rank'),
      size: 50,
      cell: ({ row: _row }) => (
        <div className="h-[46px] flex items-center">
          <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
            {_row.original.rank}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'tier',
      header: t('tier'),
      size: 100,
      cell: ({ row: _row }) => (
        <div>{_row.original.tier && <TierBadge tier={_row.original.tier} />}</div>
      ),
    },
    {
      accessorKey: 'wallet_address',
      header: t('trader'),
      size: 100,
      cell: ({ row: _row }) => {
        return (
          <div className="flex gap-[8px]">
            <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
              {ellipseAddress(_row.original.wallet_address)}{' '}
              {_row.original.wallet_address === address ? '(You)' : ''}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: 'xp',
      header: 'XP',
      size: 100,
      cell: ({ row: _row }) => (
        <p className="tracking-wider group-hover:text-cyan-300 transition-colors">
          {_row.original.points?.toLocaleString('en-US', {
            maximumFractionDigits: 4,
            minimumFractionDigits: 0,
          })}
        </p>
      ),
    },
  ];
  useEffect(() => {
    if (availableSeason?.leaderboards && availableSeason.leaderboards.length > 0 && !selectedWeek) {
      setSelectedWeek(availableSeason.leaderboards[0]);
    }
  }, [availableSeason?.leaderboards]);

  const currentUserRank = useMemo(() => {
    const { emoji, color } = emojiAvatarForAddress('');
    let leaderboard;
    if (dataType === 'isAll') {
      leaderboard = AllTimeLeaderboardData;
    } else if (dataType === 'isCurrentWeek') {
      leaderboard = xpLeaderboardData;
    } else if (dataType === 'isFirstWeek') {
      leaderboard = firstWeekLeaderboard;
    } else {
      leaderboard = leaderboardRawData;
    }

    return {
      emoji,
      color,
      walletAddress: leaderboard?.user?.wallet_address,
      accountValue: leaderboard?.user?.account_equity,
      points: leaderboard?.user?.points,
      pnl: leaderboard?.user?.pnl,
      roi: leaderboard?.user?.roi,
      volume: leaderboard?.user?.volume,
      rank: leaderboard?.user?.rank,
      tier: leaderboard?.user?.tier,
    };
  }, [
    leaderboardRawData,
    dataType,
    xpLeaderboardData,
    firstWeekLeaderboard,
    AllTimeLeaderboardData,
  ]);

  const availableOptions = useMemo(() => {
    if (dashboardTimeRange.isNotFirstWeek) {
      return [
        {
          value: 'all',
          label: 'All Time',
        },
        {
          value: 'currentWeek',
          label: `${t('currentWeek')} (${availableSeason?.leaderboards ? moment(dashboardTimeRange?.startTime).format('MM/DD') : '10/01'} - ${availableSeason?.leaderboards ? moment(dashboardTimeRange?.endDate).format('MM/DD') : '10/08'})`,
        },
      ];
    }

    return [
      {
        value: 'all',
        label: 'All Time',
      },
      {
        label: `${t('currentWeek')} (${moment(firstWeekLeaderboard?.start_date).format('MM/DD')} - ${moment(firstWeekLeaderboard?.end_date).format('MM/DD')})`,
        value: 'firstWeek',
      },
    ];
  }, [availableSeason, dashboardTimeRange]);

  const selectedOption = useMemo(() => {
    if (!dashboardTimeRange.isNotFirstWeek) {
      if (dataType === 'isAll') {
        return 'all';
      } else if (dataType === 'isFirstWeek') {
        return 'firstWeek';
      } else if (dataType === 'isCurrentWeek') {
        return 'currentWeek';
      }
    }

    if (dataType === 'isAll') {
      return 'all';
    } else if (dataType === 'isCurrentWeek') {
      return 'currentWeek';
    } else if (dataType === 'isPreseason') {
      return 'preseason';
    }
    return selectedWeek?.id;
  }, [dashboardTimeRange, dataType, selectedWeek]);

  const userData = useMemo(() => {
    return {
      score: userScore?.total_score ?? 0,
      points: userScore?.total_vpoint ?? 0,
      claimable: userScore?.has_claimable_score,
    };
  }, [userScore]);

  const handleClaimPoints = async () => {
    setIsExploding(true);
    setIsShaking(true);

    const particleTypes = ['star', 'sparkle', 'energy', 'orb'];
    const newParticles = Array.from({ length: 40 }, (_, i) => ({
      x: Math.random() * 600 - 300,
      y: Math.random() * 600 - 300,
      delay: Math.random() * 800,
      type: particleTypes[Math.floor(Math.random() * particleTypes.length)],
    }));

    setParticles(newParticles);

    try {
      const response = await claimScore();
      setClaimedPoints(response?.claimed_points ?? 0);
      setTotalPoints(response?.total_points ?? 0);
      setOpenClaimSuccessModal(true);
      refetchAllTimeLeaderboard();
      refetchPreseasonLeaderboard();
      refetchUserScore();
      refetchCurrentWeek();
      notification.success({
        message: t('youHaveSuccessClaimedPoints')
      })
    } catch (error) {
      console.error(error);
      message.error('Failed to claim Points');
    }

    // Reset explosion after animation
    setTimeout(() => {
      setIsExploding(false);
      setIsShaking(false);
      setParticles([]);
    }, 3000); // Longer duration for more dramatic effect
  };

  const config = getTierConfig(getTierNameByPoint(userData?.points ?? 0) ?? '');

  useEffect(() => {
    if (!isLoadingAvailableSeason) {
      setTimeout(() => {
        setAvailableOptionLoading(false);
      }, 1000);
    }
  }, [isLoadingAvailableSeason]);

  return (
    <div
      className={cn([
        'w-[100%] h-full min-h-[calc(100vh-150px)] flex justify-center items-center',
        isShaking ? 'animate-pulse' : '',
      ])}
    >
      <div className="max-w-[1200px] h-full w-full px-[10px]">
        {isAuthenticated ? (
          <div className="flex lg:flex-row flex-col items-start gap-[20px] justify-between lg:h-[300px] mb-[40px]">
            <div className="border dark:bg-gray-900/60 border-cyan-500/30 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300 shadow-2xl shadow-cyan-500/10 p-[20px] rounded-[8px] w-full lg:w-auto flex-1 h-full">
              <div className="flex gap-[10px] items-center">
                <div className="w-2 h-2 bg-blue-600 dark:bg-cyan-400 rounded-full animate-pulse"></div>
                <h3 className="font-semibold text-blue-600 dark:text-cyan-400 text-xl tracking-wider">
                  {t('points')}
                </h3>
                {getTierImageByPoint(userData?.points ?? 0) ? (
                  <div className="flex gap-[4px] items-center">
                    <img
                      className="w-[30px] h-[30px]"
                      src={getTierImageByPoint(userData?.points ?? 0)}
                    />
                    <div
                      className={`text-xs font-mono font-semibold tracking-wider bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}
                    >
                      {config.name?.toLocaleUpperCase()}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="w-full mt-[20px]">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-[6px]">
                    <span className="text-gray-400 text-sm tracking-wide">{t('points')}</span>
                    {isAuthenticated && !!userData?.points && userData?.points > 0 && (
                      <div className="cursor-pointer" onClick={() => setOpenSharePointModal(true)}>
                        <Upload size={14} />
                      </div>
                    )}
                  </div>
                  <p className="text-slate-800 dark:text-cyan-100 font-bold text-lg tracking-wider">
                    {isAuthenticated && userData?.points > 0
                      ? (userData?.points ?? 0).toLocaleString('en-US', {
                          maximumFractionDigits: 4,
                          minimumFractionDigits: 0,
                        })
                      : 0}
                  </p>
                </div>
                <div className="flex justify-between items-center mb-[20px]">
                  <div>
                    <span className="text-gray-400 text-sm tracking-wide">{t('rank')}</span>
                  </div>
                  <p className="inline-flex items-center justify-center rounded-md border text-xs w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent [a&]:hover:bg-primary/90 bg-gradient-to-r from-purple-400 to-purple-600 text-black font-bold px-3 py-1 shadow-lg shadow-yellow-500/20">
                    {isAuthenticated && AllTimeLeaderboardData?.user?.rank
                      ? '#' + AllTimeLeaderboardData?.user.rank
                      : 'N/A'}
                  </p>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-gray-400 text-sm tracking-wide">{t('volume')}</span>
                  </div>
                  <p className="text-slate-700 dark:text-cyan-100">
                    $
                    {userScore?.trade_volume
                      ? format6SignificantFigures(isAuthenticated ? userScore?.trade_volume : 0)
                      : 0}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-gray-400 text-sm tracking-wide">
                      {t('referralVolume')}
                    </span>
                  </div>
                  <p className="text-slate-700 dark:text-cyan-100">
                    $
                    {Number(isAuthenticated ? (userScore?.referral_volume ?? 0) : 0).toLocaleString(
                      'en-US',
                      {
                        maximumFractionDigits: 4,
                        minimumFractionDigits: 0,
                      },
                    )}{' '}
                    / $
                    {Number(
                      isAuthenticated ? (userScore?.referral_lp_equity ?? 0) : 0,
                    ).toLocaleString('en-US', {
                      maximumFractionDigits: 4,
                      minimumFractionDigits: 0,
                    })}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-gray-400 text-sm tracking-wide">{t('lpEquity')}</span>
                  </div>
                  <p className="text-slate-700 dark:text-cyan-100">
                    $
                    {userScore?.lp_equity
                      ? format6SignificantFigures(isAuthenticated ? userScore?.lp_equity : 0)
                      : 0}
                  </p>
                </div>
              </div>
              {isAuthenticated && (
                <div className="mt-[20px] pt-4 border-t border-cyan-500/30">
                  <div className="relative flex justify-center">
                    {isExploding && (
                      <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        <CyberpunkExplosion delay={0} />
                        <CyberpunkExplosion delay={400} />
                        <CyberpunkExplosion delay={800} />

                        <DigitalGlitch delay={200} />
                        <DigitalGlitch delay={600} />

                        <MatrixRain delay={100} />
                        <MatrixRain delay={500} />

                        <DigitalWave delay={300} />
                        <DigitalWave delay={700} />
                        {/* Enhanced particles */}
                        {particles.map((particle, i) => (
                          <CyberpunkParticle
                            key={i}
                            x={particle.x}
                            y={particle.y}
                            delay={particle.delay}
                            type={particle.type}
                          />
                        ))}

                        <div
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-80 animate-ping shadow-2xl shadow-cyan-400/50"
                          style={{
                            clipPath:
                              'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                          }}
                        />
                        <div
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-r from-green-400 via-cyan-500 to-blue-500 opacity-90 animate-pulse shadow-2xl shadow-green-400/50"
                          style={{
                            clipPath:
                              'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                          }}
                        />
                        <div
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white opacity-95 animate-bounce shadow-2xl shadow-white/50"
                          style={{
                            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                          }}
                        />

                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-40 bg-gradient-to-b from-cyan-400 via-purple-500 to-transparent rotate-45 animate-pulse shadow-lg shadow-cyan-400/50" />
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-40 bg-gradient-to-b from-pink-400 via-blue-500 to-transparent -rotate-45 animate-pulse shadow-lg shadow-pink-400/50" />
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-2 bg-gradient-to-r from-green-400 via-cyan-500 to-transparent animate-pulse shadow-lg shadow-green-400/50" />
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-2 bg-gradient-to-l from-purple-400 via-pink-500 to-transparent animate-pulse shadow-lg shadow-purple-400/50" />
                      </div>
                    )}
                    <button
                      onClick={handleClaimPoints}
                      disabled={
                        isExploding ||
                        !userData.claimable ||
                        allTimeDataFetching ||
                        preseasonDataFetching
                      }
                      className={`
                      relative font-bold tracking-wider px-[20px] py-[10px] text-black
                      bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500
                      hover:from-cyan-300 hover:via-purple-400 hover:to-pink-400
                      shadow-lg shadow-cyan-500/50 hover:shadow-purple-400/80
                      border-0 transition-all duration-300 rounded-[8px]
                      ${isExploding ? 'scale-150 animate-bounce shadow-2xl shadow-purple-400/90' : 'hover:scale-110'}
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                      disabled:shadow-none
                      disabled:hover:scale-100
                      disabled:hover:from-cyan-400
                      disabled:hover:via-purple-500
                      disabled:hover:to-pink-500
                    `}
                    >
                      {isExploding ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          {t('claiming')}...
                        </span>
                      ) : (
                        t('claimPoints')
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div
              className={cn([
                'flex-col flex justify-between gap-[10px]',
                ' w-full lg:h-[300px] lg:w-[600px] p-[10px] border dark:bg-gray-900/60 border-cyan-500/30 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300 shadow-2xl shadow-cyan-500/10 rounded-xl',
              ])}
            >
              <div className="flex items-center gap-[10px]">
                <div className="w-2 h-2 bg-blue-600 dark:bg-cyan-400 rounded-full animate-pulse"></div>
                <h3 className="font-semibold text-blue-600 dark:text-cyan-400 text-xl tracking-wider">
                  {t('score')}
                </h3>
              </div>
              <div className="flex justify-center items-center gap-[80px] flex-wrap">
                <div className="h-[240px]">
                  {isAuthenticated ? (
                    <CustomPieChart
                      size={240}
                      score={isAuthenticated ? (userData?.score ?? 0) : 0}
                      data={[
                        {
                          name: t('traderScore'),
                          value: userScore?.trader_score ?? 0,
                          color: '#00F0FF',
                        },
                        { name: t('lpScore'), value: userScore?.lp_score ?? 0, color: '#FF4DFF' },
                        {
                          name: t('directReferralScore'),
                          value: BigNumber(userScore?.direct_referral_score ?? 0)
                            .plus(userScore?.lp_referral_score ?? 0)
                            .toNumber(),
                          color: '#7CFF00',
                        },
                        { name: t('indirectReferralScore'), value: 0, color: '#FFD500' },
                      ]}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="mb-[20px]">
          {availableOptionLoading ? (
            <Skeleton.Input active={true} className="w-[200px] h-[32px] rounded-md" />
          ) : (
            <Select
              value={selectedOption}
              onValueChange={(val: string) => {
                if (val === 'all') {
                  setDataType('isAll');
                } else if (val === 'currentWeek') {
                  setDataType('isCurrentWeek');
                } else if (val === 'firstWeek') {
                  setDataType('isFirstWeek');
                } else if (val === 'preseason') {
                  setDataType('isPreseason');
                }

                const foundSeason = availableSeason?.leaderboards?.find((item) => item.id === val);
                if (foundSeason) {
                  setSelectedWeek(foundSeason);
                  setDataType('isPreviousWeek');
                }

                setPage(1);
              }}
            >
              <SelectTrigger className="data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-xs outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 w-80 dark:bg-gray-900/80 border-cyan-500/30 dark:text-cyan-100 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableOptions.map((item) => {
                  return (
                    <SelectItem
                      className="focus:text-accent-foreground [&amp;_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:shrink-0 [&amp;_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 dark:text-cyan-100 focus:bg-cyan-500/20"
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>
        {isMobile ? (
          <div className="mb-[40px] flex flex-col gap-[10px]">
            {leaderBoardData?.participants.map((item) => {
              return (
                <CardItem
                  key={item.user_id}
                  isCurrentWeek={dataType === 'isCurrentWeek'}
                  data={item}
                />
              );
            })}
            <div className="mt-[10px] flex justify-center">
              <Pagination
                showSizeChanger={false}
                total={leaderBoardData?.total || 1}
                onChange={setPage}
                current={page}
                hideOnSinglePage={true}
                pageSize={pageSize}
                simple
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 rounded-xl border py-6 px-6 dark:bg-gray-900/60 border-cyan-500/30 backdrop-blur-sm shadow-2xl shadow-cyan-500/10">
            <div className="flex items-center gap-[10px]">
              <div className="w-2 h-2 bg-blue-600 dark:bg-cyan-400 rounded-full animate-pulse"></div>
              <h3 className="font-semibold text-blue-600 dark:text-cyan-400 text-xl tracking-wider">
                {t('leaderboard')}
              </h3>
            </div>
            <div className="flex">
              <div className="flex-1">
                <Table
                  loading={allTimeDataFetching || preseasonDataFetching || xpLeaderBoardFetching}
                  data={leaderBoardData?.participants ?? []}
                  columns={dataType === 'isCurrentWeek' ? xpLeaderboardColumns : columns}
                  pagination={{
                    totalPage: Math.ceil(
                      (leaderBoardData?.total || 1) / (leaderBoardData?.page_size ?? 1),
                    ),
                    page: leaderBoardData?.page_number ?? 1,
                    pageSize: leaderBoardData?.page_size ?? 1,
                    onPageChange: setPage,
                  }}
                  stickyRow={
                    isAuthenticated && currentUserRank.walletAddress ? (
                      <tr className="hover:bg-muted/50 data-[state=selected]:bg-muted border-b border-cyan-500/10 transition-all duration-300 group bg-gradient-to-r from-cyan-500/20 via-blue-500/15 to-purple-500/20 border-l-4 border-l-cyan-400 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-400/30">
                        <td colSpan={1} className="py-[2px] px-2 h-[51px]">
                          <div className="flex gap-[8px]">
                            <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                              {currentUserRank.rank}
                            </p>
                          </div>
                        </td>
                        <td colSpan={1} className="py-[2px] px-2">
                          {currentUserRank.tier && <TierBadge tier={currentUserRank.tier} />}
                        </td>
                        <td colSpan={1} className="py-[2px] px-2">
                          <div className="flex gap-[8px]">
                            <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                              {ellipseAddress(currentUserRank.walletAddress)} (You)
                            </p>
                          </div>
                        </td>

                        {/* <td colSpan={1} className="py-[2px] px-2">
                          <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                            ${format6SignificantFigures(currentUserRank.accountValue ?? 0)}
                          </p>
                        </td> */}
                        {/* <td colSpan={1} className="py-[2px] px-2">
                          <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                            ${format6SignificantFigures(currentUserRank.pnl ?? 0)}
                          </p>
                        </td> */}
                        {/* <td colSpan={1} className="py-[2px] px-2">
                          <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                            {nFormatterV2(currentUserRank.roi?.toString() ?? '0', 2)}%
                          </p>
                        </td> */}
                        <td colSpan={1} className="py-[2px] px-2">
                          <p className="tracking-wider group-hover:text-cyan-300 transition-colors text-blue-600 dark:text-cyan-200">
                            {currentUserRank?.points?.toLocaleString('en-US', {
                              maximumFractionDigits: 4,
                              minimumFractionDigits: 0,
                            })}
                          </p>
                        </td>
                      </tr>
                    ) : null
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {!!userData.points && userData.points > 0 && (
        <SharePointModal
          data={{
            ...(AllTimeLeaderboardData?.user as any),
            points: Number(userData.points < 0 ? 0 : userData.points),
          }}
          open={openSharePointModal}
          onClose={() => setOpenSharePointModal(false)}
        />
      )}
      {!!claimedPoints && claimedPoints > 0 && (
        <CelebrationModal
          points={claimedPoints}
          open={openClaimSuccessModal}
          totalPoints={totalPoints ?? 0}
          onClose={() => {
            setOpenClaimSuccessModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TradingLeaderboard;
